import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { take, switchMap } from 'rxjs';
import { ToastService } from '../../ui';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDialogComponent } from '../../shared/confirm-dialog/confirm-dialog.component';
import { AuthService } from '../../core/auth/auth.service';
import { ContactsService } from './contacts.service';
import { Contact, ForwardingTarget, NotificationTarget, TargetPayload } from './contacts.model';
import { ContactFormDialogComponent, ContactFormResult } from './dialogs/contact-form-dialog.component';
import { TargetDialogComponent, TargetDialogData, TargetKind } from './dialogs/target-dialog.component';
import { extractErrorMessage } from '../../shared/utils/extract-error-message';
import { ErrorStateComponent } from '../../shared/error-state/error-state.component';
import {
  HlmBadgeComponent,
  HlmBtnDirective,
  HlmIconComponent,
  HlmInputDirective,
  HlmSwitchComponent,
  HlmSpinnerComponent,
  HlmTooltipDirective,
  HLM_FORM_FIELD,
} from '../../ui';

@Component({
  selector: 'cmt-contacts-list',
  imports: [
    ReactiveFormsModule,
    ErrorStateComponent,
    HlmBadgeComponent, HlmBtnDirective, HlmIconComponent,
    HlmInputDirective, HlmSwitchComponent, HlmSpinnerComponent,
    HlmTooltipDirective,
    ...HLM_FORM_FIELD,
  ],
  templateUrl: './contacts-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ContactsListComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly contactsService = inject(ContactsService);
  private readonly auth = inject(AuthService);
  private readonly toast = inject(ToastService);
  private readonly dialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);

  readonly contacts = signal<Contact[]>([]);
  readonly loading = signal(false);
  readonly error = signal<string | null>(null);

  // Filter (#10)
  readonly filterText = signal('');
  readonly filteredContacts = computed(() => {
    const q = this.filterText().toLowerCase().trim();
    if (!q) return this.contacts();
    return this.contacts().filter(c => {
      const name = (c.firstName || '').toLowerCase();
      const last = (c.lastName || '').toLowerCase();
      const company = (c.name || '').toLowerCase();
      return name.includes(q) || last.includes(q) || company.includes(q);
    });
  });

  private phoneServiceId = 0;

  // --- Inline EDIT state (only contact-edit is inline; add + targets use dialogs) ---
  readonly editingContactId = signal<number | null>(null);
  readonly contactSaving = signal(false);

  /** Tracks expanded contact panels — preserved across reload (e.g. after reorder) */
  readonly expandedContactIds = signal<Set<number>>(new Set());

  // --- Reactive form for inline contact-edit ---
  readonly contactForm = new FormGroup({
    isPerson: new FormControl(true, { nonNullable: true }),
    salutation: new FormControl('', { nonNullable: true }),
    title: new FormControl('', { nonNullable: true }),
    firstName: new FormControl('', { nonNullable: true }),
    lastName: new FormControl('', { nonNullable: true }),
    name: new FormControl('', { nonNullable: true }),
    email: new FormControl('', { nonNullable: true }),
    description: new FormControl('', { nonNullable: true }),
    department: new FormControl('', { nonNullable: true }),
    position: new FormControl('', { nonNullable: true }),
    notifyDefaultDestination: new FormControl(false, { nonNullable: true }),
  });

  ngOnInit(): void {
    this.route.params.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      const id = Number(params['phoneServiceId']);
      if (id) {
        this.phoneServiceId = id;
        this.loadContacts();
      }
    });
  }

  loadContacts(): void {
    this.loading.set(true);
    this.error.set(null);
    this.contactsService.getContacts(this.phoneServiceId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (contacts) => {
          contacts.sort((a, b) => this.getContactDisplayName(a).localeCompare(this.getContactDisplayName(b)));
          this.contacts.set(contacts);
          this.loading.set(false);
        },
        error: (err) => {
          this.error.set(extractErrorMessage(err, 'Failed to load contacts'));
          this.loading.set(false);
        },
      });
  }

  // --- Display helpers ---

  getContactDisplayName(contact: Contact): string {
    if (contact.isPerson) {
      const parts = [contact.lastName, contact.firstName].filter(Boolean);
      return parts.join(', ') || 'Unnamed Person';
    }
    return contact.name || 'Unnamed Organization';
  }

  // --- Expansion state tracking (persists across reloads) ---

  isExpanded(contactId: number): boolean {
    return this.expandedContactIds().has(contactId);
  }

  onPanelOpened(contactId: number): void {
    this.expandedContactIds.update(set => {
      const next = new Set(set);
      next.add(contactId);
      return next;
    });
  }

  onPanelClosed(contactId: number): void {
    this.expandedContactIds.update(set => {
      const next = new Set(set);
      next.delete(contactId);
      return next;
    });
  }

  /** Mutex: only one inline edit at a time across page (covers contact edit only — targets use modal dialog). */
  readonly isAnyEditActive = computed(() => this.editingContactId() !== null);

  /** Pre-computed display names keyed by contact id — avoids per-CD recalculation in panel headers */
  readonly contactDisplayNames = computed<Map<number, string>>(() => {
    const map = new Map<number, string>();
    for (const c of this.contacts()) {
      map.set(c.id, this.getContactDisplayName(c));
    }
    return map;
  });

  // --- Contact CRUD ---

  /** Open the styled dialog to add a new contact. */
  openAddContactDialog(): void {
    if (this.isAnyEditActive()) return;
    const ref = this.dialog.open(ContactFormDialogComponent, {
      panelClass: 'cmt-dialog-panel',
      data: { phoneServiceId: this.phoneServiceId },
      autoFocus: 'first-tabbable',
    });
    ref.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(result => {
      if (!result) return;
      this.createContactFromDialog(result);
    });
  }

  private createContactFromDialog(result: ContactFormResult): void {
    const payload: Partial<Contact> & { phoneServiceId: number } = {
      phoneServiceId: this.phoneServiceId,
      ...result,
    };
    this.auth.user$.pipe(
      take(1),
      switchMap(user => this.contactsService.createContact(payload, user?.login ?? 'unknown')),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe({
      next: () => {
        this.loadContacts();
        this.toast.open('Contact created', 'OK', { duration: 3000 });
      },
      error: (err) => {
        this.toast.open(extractErrorMessage(err, 'Failed to create contact'), 'OK', { duration: 6000, tone: 'error' });
      },
    });
  }

  /** Inline edit-mode entry. */
  startEditContact(contact: Contact): void {
    if (this.isAnyEditActive()) return;
    this.contactForm.reset({
      isPerson: contact.isPerson,
      salutation: contact.salutation || '',
      title: contact.title || '',
      firstName: contact.firstName || '',
      lastName: contact.lastName || '',
      name: contact.name || '',
      email: contact.email || '',
      description: contact.description || '',
      department: contact.department || '',
      position: contact.position || '',
      notifyDefaultDestination: contact.notifyDefaultDestination,
    });
    this.editingContactId.set(contact.id);
    this.expandedContactIds.update(set => new Set(set).add(contact.id));
  }

  cancelContactForm(): void {
    this.editingContactId.set(null);
  }

  saveContact(): void {
    const editingId = this.editingContactId();
    if (editingId === null) return;
    this.contactSaving.set(true);
    const v = this.contactForm.getRawValue();
    const payload: Partial<Contact> = {
      id: editingId,
      phoneServiceId: this.phoneServiceId,
      isPerson: v.isPerson,
      salutation: v.isPerson ? v.salutation : null,
      title: v.isPerson ? v.title : null,
      firstName: v.isPerson ? v.firstName : null,
      lastName: v.isPerson ? v.lastName : null,
      name: v.isPerson ? null : v.name,
      email: v.email,
      description: v.description,
      department: v.department,
      position: v.position,
      notifyDefaultDestination: v.notifyDefaultDestination,
    };
    this.contactsService.updateContact(payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.editingContactId.set(null);
          this.contactSaving.set(false);
          this.loadContacts();
          this.toast.open('Contact updated', 'OK', { duration: 3000 });
        },
        error: (err) => {
          this.contactSaving.set(false);
          this.toast.open(extractErrorMessage(err, 'Failed to update contact'), 'OK', { duration: 6000, tone: 'error' });
        },
      });
  }

  deleteContact(contact: Contact): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Delete Contact',
        message: `Are you sure you want to delete "${this.getContactDisplayName(contact)}"? All forwarding and notification targets will also be removed.`,
        confirmLabel: 'Delete',
        confirmColor: 'warn',
        icon: 'warning',
      },
    });

    dialogRef.afterClosed().subscribe(confirmed => {
      if (!confirmed) return;
      this.auth.user$.pipe(
        take(1),
        switchMap(user => this.contactsService.deleteContact(contact.id, user?.login ?? 'unknown')),
        takeUntilDestroyed(this.destroyRef),
      ).subscribe({
        next: () => {
          this.contacts.update(c => c.filter(ct => ct.id !== contact.id));
          this.expandedContactIds.update(set => {
            const next = new Set(set);
            next.delete(contact.id);
            return next;
          });
          this.toast.open('Contact deleted', 'OK', { duration: 3000 });
        },
        error: (err) => {
          this.toast.open(extractErrorMessage(err, 'Failed to delete contact'), 'OK', { duration: 6000, tone: 'error' });
        },
      });
    });
  }

  // --- Target CRUD (all dialog-based now) ---

  openAddTargetDialog(contact: Contact, kind: TargetKind): void {
    if (this.isAnyEditActive()) return;
    const data: TargetDialogData = {
      kind,
      mode: 'add',
      contactId: contact.id,
      contactName: this.getContactDisplayName(contact),
    };
    const ref = this.dialog.open(TargetDialogComponent, {
      panelClass: 'cmt-dialog-panel',
      data,
      autoFocus: 'first-tabbable',
    });
    ref.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(payload => {
      if (!payload) return;
      this.persistTarget(payload, contact, kind, ref.componentInstance ?? null);
    });
  }

  openEditTargetDialog(contact: Contact, target: ForwardingTarget | NotificationTarget, kind: TargetKind): void {
    if (this.isAnyEditActive()) return;
    const data: TargetDialogData = {
      kind,
      mode: 'edit',
      contactId: contact.id,
      contactName: this.getContactDisplayName(contact),
      existing: target,
    };
    const ref = this.dialog.open(TargetDialogComponent, {
      panelClass: 'cmt-dialog-panel',
      data,
      autoFocus: 'first-tabbable',
    });
    ref.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(payload => {
      if (!payload) return;
      this.persistTarget(payload, contact, kind, ref.componentInstance ?? null);
    });
  }

  private persistTarget(payload: TargetPayload, contact: Contact, kind: TargetKind, dialogInstance: TargetDialogComponent | null): void {
    const isEdit = payload.id !== undefined;
    const obs = isEdit
      ? this.contactsService.updateTarget(payload)
      : this.contactsService.createTarget(payload);

    obs.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.loadContacts();
        const verb = isEdit ? 'updated' : (kind === 'forwarding' ? 'added' : 'added');
        const noun = kind === 'forwarding' ? 'Forwarding target' : 'Notification target';
        this.toast.open(`${noun} ${verb}`, 'OK', { duration: 3000 });
      },
      error: (err) => {
        const msg = extractErrorMessage(err, `Failed to ${isEdit ? 'update' : 'add'} ${kind} target`);
        // Long, visible snackbar for now (dialog already closed)
        this.toast.open(msg, 'OK', { duration: 7000, tone: 'error' });
      },
    });
  }

  deleteTarget(contact: Contact, target: ForwardingTarget | NotificationTarget, kind: TargetKind): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Delete Target',
        message: `Delete ${kind === 'forwarding' ? 'forwarding' : 'notification'} target "${target.name}"?`,
        confirmLabel: 'Delete',
        confirmColor: 'warn',
        icon: 'warning',
      },
    });

    dialogRef.afterClosed().subscribe(confirmed => {
      if (!confirmed) return;
      const targetType = kind === 'forwarding' ? 'FORWARDING' : 'NOTIFICATION';
      this.contactsService.deleteTarget(target.id, targetType)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => {
            if (kind === 'forwarding') {
              contact.forwardingTargets = contact.forwardingTargets.filter(t => t.id !== target.id);
            } else {
              contact.notificationTargets = contact.notificationTargets.filter(t => t.id !== target.id);
            }
            this.contacts.update(c => [...c]);
            this.toast.open('Target deleted', 'OK', { duration: 3000 });
          },
          error: (err) => {
            this.toast.open(extractErrorMessage(err, 'Failed to delete target'), 'OK', { duration: 6000, tone: 'error' });
          },
        });
    });
  }

  // --- Target reordering (forwarding only) ---

  moveTargetUp(contact: Contact, target: ForwardingTarget): void {
    if (this.isAnyEditActive()) return;
    this.contactsService.switchUp(contact.id, target.sortNumber)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.loadContacts(),
        error: () => this.toast.open('Failed to reorder', 'OK', { duration: 3000 }),
      });
  }

  moveTargetDown(contact: Contact, target: ForwardingTarget): void {
    if (this.isAnyEditActive()) return;
    this.contactsService.switchDown(contact.id, target.sortNumber)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.loadContacts(),
        error: () => this.toast.open('Failed to reorder', 'OK', { duration: 3000 }),
      });
  }

}
